import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';

import { OperationService } from '../../../services';
import type { EnhancementStrength, OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_FACE_ENHANCE_STATE,
  buildFaceEnhanceRequest,
  hasSourceImage,
  type FaceEnhanceFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  onResult?: (result: OperationResult) => void;
}

const LEVEL_OPTIONS: EnhancementStrength[] = ['Low', 'Light', 'Medium', 'High', 'Strong'];

export const FaceEnhancePanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  onResult,
}) => {
  const [state, setState] = useState<FaceEnhanceFormState>(DEFAULT_FACE_ENHANCE_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.faceEnhance);

  const hasSource = hasSourceImage(source);

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to enhance faces.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildFaceEnhanceRequest(state, sid, source);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled = mutation.isPending || isCreatingSession || !hasSource;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <FaceRetouchingNaturalIcon fontSize="small" />
        <Typography variant="subtitle2">Face enhance</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image with faces to enhance.</Alert>
      )}

      <FormControl size="small" fullWidth>
        <InputLabel id="face-enhance-level-label">Enhancement level</InputLabel>
        <Select
          labelId="face-enhance-level-label"
          label="Enhancement level"
          value={state.enhancementLevel}
          onChange={e =>
            setState(s => ({ ...s, enhancementLevel: e.target.value as EnhancementStrength }))
          }
        >
          {LEVEL_OPTIONS.map(opt => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Switch
            checked={state.preserveIdentity}
            onChange={e => setState(s => ({ ...s, preserveIdentity: e.target.checked }))}
            size="small"
          />
        }
        label="Preserve identity"
      />

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Face enhance failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <FaceRetouchingNaturalIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Enhancing…' : 'Enhance faces'}
        </Button>
      </Box>
    </Stack>
  );
};
